// Hypothetical double-elimination bracket + sequential schedule.
//
// Assumptions (shown on the page as well):
// - Teams are seeded 1..N; the bracket is padded to 8 or 16 slots and the top
//   seeds receive byes.
// - Upper Final and Lower Final (the semifinals) and the Grand Final are Bo5;
//   every other match is Bo3. No grand-final bracket reset.
// - Each Bo3 is budgeted at 1 hour and each Bo5 at 2 hours; matches run
//   back-to-back from each play day's start.
// - No match may start later than 5:30pm PT (7.5h after the 10am PT start).
//   Matches run one at a time unless that limit would be broken; then whole
//   early rounds (earliest first) run as two concurrent streams. Bo5 matches
//   always run alone.
// - Within those rules, days are balanced so the last start is as early as possible.

// Budgeted match length by format.
export const BO3_MINUTES = 60
export const BO5_MINUTES = 120
export const LATEST_START_MINUTES = 7.5 * 60
const STREAMS = 2
const STEP = 30
export const MIN_TEAMS = 11
export const MAX_TEAMS = 16

const BYE = { type: 'bye' }

// Standard seed order so that 1 meets the lowest seed and 1/2 only meet in the final.
const seedOrder = (size) => {
  let order = [1]
  while (order.length < size) {
    const n = order.length * 2
    order = order.flatMap((s) => [s, n + 1 - s])
  }
  return order
}

const buildStructure = (size) => {
  const k = Math.log2(size)
  const matches = {}
  const add = (id, props) => {
    matches[id] = { id, ...props }
    return id
  }
  const winner = (id) => ({ type: 'winner', id })
  const loser = (id) => ({ type: 'loser', id })

  // Upper bracket
  const ub = []
  const order = seedOrder(size)
  ub.push(
    Array.from({ length: size / 2 }, (_, i) =>
      add(`U1-${i + 1}`, {
        bracket: 'upper',
        round: 1,
        a: { type: 'seed', seed: order[i * 2] },
        b: { type: 'seed', seed: order[i * 2 + 1] },
      })
    )
  )
  for (let r = 2; r <= k; r++) {
    const prev = ub[r - 2]
    ub.push(
      Array.from({ length: prev.length / 2 }, (_, i) =>
        add(`U${r}-${i + 1}`, {
          bracket: 'upper',
          round: r,
          a: winner(prev[i * 2]),
          b: winner(prev[i * 2 + 1]),
        })
      )
    )
  }

  // Lower bracket: odd rounds pair lower-bracket survivors (round 1 pairs upper
  // round 1 losers); even rounds take the losers dropping from the upper bracket.
  // Anti-rematch dropdown seeding: the drop order is reversed on the first drop
  // round and every other one after (r = 2, 6, 10, …) so a team dropping from the
  // upper bracket never lands back on an opponent it just played. This removes
  // immediate lower-bracket rematches across all sizes 8–16.
  const lb = []
  const ubR1 = ub[0]
  lb.push(
    Array.from({ length: ubR1.length / 2 }, (_, i) =>
      add(`L1-${i + 1}`, {
        bracket: 'lower',
        round: 1,
        a: loser(ubR1[i * 2]),
        b: loser(ubR1[i * 2 + 1]),
      })
    )
  )
  for (let r = 2; r <= 2 * (k - 1); r++) {
    const prev = lb[r - 2]
    if (r % 2 === 0) {
      const dropRound = ub[r / 2]
      const drops = (r / 2) % 2 === 1 ? [...dropRound].reverse() : dropRound
      lb.push(
        prev.map((p, i) =>
          add(`L${r}-${i + 1}`, { bracket: 'lower', round: r, a: winner(p), b: loser(drops[i]) })
        )
      )
    } else {
      lb.push(
        Array.from({ length: prev.length / 2 }, (_, i) =>
          add(`L${r}-${i + 1}`, {
            bracket: 'lower',
            round: r,
            a: winner(prev[i * 2]),
            b: winner(prev[i * 2 + 1]),
          })
        )
      )
    }
  }

  const ubFinal = ub[k - 1][0]
  const lbFinal = lb[lb.length - 1][0]
  const gf = add('GF', { bracket: 'final', round: 1, a: winner(ubFinal), b: winner(lbFinal) })

  // Play order that respects every dependency:
  // U1, L1, then per upper round r: Ur, L(2r-2), L(2r-1); then GF.
  const playOrder = [...ub[0], ...lb[0]]
  for (let r = 2; r <= k; r++) {
    playOrder.push(...ub[r - 1], ...lb[2 * r - 3])
    if (r < k) playOrder.push(...lb[2 * r - 2])
  }
  playOrder.push(gf)

  return { matches, ub, lb, ubFinal, lbFinal, gf, playOrder }
}

// Resolve byes: a match missing a participant isn't played and passes the
// other side straight through.
const resolve = (structure, teams) => {
  const { matches } = structure
  const memo = {}
  const side = (ref) => {
    if (ref.type === 'seed') return ref.seed <= teams ? ref : BYE
    const m = outcome(ref.id)
    if (m.played) return ref
    return ref.type === 'winner' ? m.advances : BYE
  }
  const outcome = (id) => {
    if (memo[id]) return memo[id]
    const m = matches[id]
    const a = side(m.a)
    const b = side(m.b)
    let result
    if (a.type === 'bye' && b.type === 'bye') result = { played: false, advances: BYE }
    else if (a.type === 'bye') result = { played: false, advances: b }
    else if (b.type === 'bye') result = { played: false, advances: a }
    else result = { played: true, a, b }
    memo[id] = result
    return result
  }
  Object.keys(matches).forEach(outcome)
  return memo
}

const roundName = (m, structure, round) => {
  if (m.id === 'GF') return 'Grand Final'
  if (m.id === structure.ubFinal) return 'Upper Final'
  if (m.id === structure.lbFinal) return 'Lower Final'
  return `${m.bracket === 'upper' ? 'Upper' : 'Lower'} Round ${round}`
}

// Greedily place matches (in play order) into days. A match moves to the next
// day if it can't start within `limit` minutes of the day's start. Matches in
// `parallel` share STREAMS streams; all others need every stream free.
const place = (list, dayStarts, parallel, limit) => {
  const slots = []
  const at = {}
  let day = 0
  let free = Array(STREAMS).fill(dayStarts[0])
  for (const m of list) {
    for (;;) {
      const dayStart = dayStarts[day]
      // Dependencies from earlier days are already finished.
      const sameDayEnds = m.deps.map((id) => at[id]).filter((d) => d.start >= dayStart).map((d) => d.end)
      const depReady = Math.max(dayStart, ...sameDayEnds)
      const stream = parallel.has(m.id) ? free.indexOf(Math.min(...free)) : -1
      const start = Math.max(depReady, stream >= 0 ? free[stream] : Math.max(...free))
      if (start - dayStart <= limit * 60000) {
        const end = start + m.minutes * 60000
        if (stream >= 0) free[stream] = end
        else free = free.map(() => end)
        at[m.id] = { day, start, end, stream }
        slots.push(m.id)
        break
      }
      day++
      if (day >= dayStarts.length) return null
      free = Array(STREAMS).fill(dayStarts[day])
    }
  }
  return at
}

// Smallest prefix of whole early rounds to run concurrently, then the earliest
// possible latest start.
const schedule = (list, dayStarts) => {
  const rounds = []
  list.forEach((m) => {
    const key = `${m.bracket}-${m.round}`
    if (rounds.length && rounds[rounds.length - 1].key === key) rounds[rounds.length - 1].ids.push(m.id)
    else rounds.push({ key, ids: [m.id] })
  })
  const eligibleRounds = rounds.filter((r) => list.find((m) => m.id === r.ids[0]).bestOf === 3)

  let fallback = null
  for (let n = 0; n <= eligibleRounds.length; n++) {
    const parallel = new Set(eligibleRounds.slice(0, n).flatMap((r) => r.ids))
    for (let limit = 0; limit <= 24 * 60; limit += STEP) {
      const at = place(list, dayStarts, parallel, limit)
      if (!at) continue
      if (limit <= LATEST_START_MINUTES) return at
      if (!fallback || limit < fallback.limit) fallback = { at, limit }
      break
    }
  }
  return fallback.at
}

export const buildBracket = (teams, playDays) => {
  const size = teams <= 8 ? 8 : 16
  const structure = buildStructure(size)
  const outcomes = resolve(structure, teams)
  const bo5 = new Set([structure.ubFinal, structure.lbFinal, structure.gf])

  const played = structure.playOrder.filter((id) => outcomes[id].played)
  const number = Object.fromEntries(played.map((id, i) => [id, i + 1]))

  // Renumber rounds so rounds emptied entirely by byes don't leave gaps.
  const displayRound = {}
  ;[['upper', structure.ub], ['lower', structure.lb]].forEach(([bracket, rounds]) => {
    let n = 0
    rounds.forEach((ids, i) => {
      if (ids.some((id) => outcomes[id].played)) displayRound[`${bracket}-${i + 1}`] = ++n
    })
  })

  const label = (ref) => {
    if (ref.type === 'seed') return `Seed ${ref.seed}`
    return `${ref.type === 'winner' ? 'Winner' : 'Loser'} of M${number[ref.id]}`
  }

  const list = played.map((id) => {
    const m = structure.matches[id]
    const bestOf = bo5.has(id) ? 5 : 3
    return {
      id,
      number: number[id],
      bracket: m.bracket,
      round: displayRound[`${m.bracket}-${m.round}`],
      name: roundName(m, structure, displayRound[`${m.bracket}-${m.round}`]),
      bestOf,
      minutes: bestOf === 5 ? BO5_MINUTES : BO3_MINUTES,
      a: label(outcomes[id].a),
      b: label(outcomes[id].b),
      deps: [outcomes[id].a, outcomes[id].b].filter((r) => r.type !== 'seed').map((r) => r.id),
    }
  })

  // Schedule
  const dayStarts = playDays.map((d) => new Date(d.start).getTime())
  const at = schedule(list, dayStarts)
  list.forEach((m) => {
    m.day = at[m.id].day
    m.start = new Date(at[m.id].start)
    m.end = new Date(at[m.id].end)
    m.stream = at[m.id].stream
  })
  const days = playDays.map((d, i) => {
    const dayMatches = list
      .filter((m) => m.day === i)
      .sort((x, y) => x.start - y.start || x.number - y.number)
    dayMatches.forEach((m) => {
      m.concurrent = dayMatches.some((o) => o !== m && o.start < m.end && m.start < o.end)
    })
    return {
      ...d,
      matches: dayMatches,
      end: new Date(Math.max(dayStarts[i], ...dayMatches.map((m) => m.end.getTime()))),
    }
  })

  // Columns for the bracket diagram (only rounds that contain played matches).
  const columns = (rounds) =>
    rounds
      .map((ids) => ids.filter((id) => outcomes[id].played).map((id) => list[number[id] - 1]))
      .filter((col) => col.length)

  return {
    teams,
    size,
    matches: list,
    days,
    upper: columns(structure.ub),
    lower: columns(structure.lb),
    final: list[number[structure.gf] - 1],
  }
}
